import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown, Minus, Loader2, RefreshCw, AlertTriangle, BookOpen } from 'lucide-react';
import Tip from '../../components/Tip';

const API_URL = process.env.REACT_APP_API_URL || 'https://tradesavvy-production.up.railway.app';

interface WatchlistItem {
  pair: string;
  symbol: string;
  name: string;
}

interface OrderBlock {
  type: 'bullish' | 'bearish';
  price: number;
  index: number;
}

interface FVGItem {
  type: 'bullish' | 'bearish';
  index: number;
}

interface StructureData {
  last_bos: { direction: string; index: number } | null;
  last_choch: { direction: string; index: number } | null;
}

interface TimeframeResult {
  signal: 'BUY' | 'SELL' | 'HOLD';
  confidence: number;
  trend: 'UPTREND' | 'DOWNTREND' | 'NEUTRAL';
  order_blocks: OrderBlock[];
  fvg: FVGItem[];
  structure: StructureData;
  entry_zone: number | null;
  stop_loss: number | null;
  take_profit: number | null;
  risk_reward: number | null;
  liquidity_levels: number[];
  conviction: 'HIGH' | 'LOW' | null;
  error?: string;
}

interface CoinResult {
  symbol: string;
  price: number;
  change24h: number;
  timeframes: Record<string, TimeframeResult>;
}

interface SmartMoneyTabProps {
  watchlist: WatchlistItem[];
  apiUrl?: string;
}

const TIMEFRAMES = ['1h', '4h', '1d'];

const SmartMoneyTab: React.FC<SmartMoneyTabProps> = ({ watchlist, apiUrl }) => {
  const [data, setData] = useState<CoinResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTf, setSelectedTf] = useState('4h');
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const url = apiUrl || API_URL;

  const fetchData = useCallback(async () => {
    if (watchlist.length === 0) return;
    setLoading(true);
    try {
      const symbols = watchlist.map(w => w.pair).join(',');
      const response = await fetch(`${url}/api/signals/smc?symbols=${symbols}`, {
        signal: AbortSignal.timeout(90000),
      });
      const result = await response.json();
      setData(Array.isArray(result) ? result : []);
      setLastRefresh(new Date());
    } catch (e) {
      console.error('SMC fetch error:', e);
    }
    setLoading(false);
  }, [watchlist, url]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 120000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const nameMap: Record<string, string> = {};
  watchlist.forEach(w => { nameMap[w.symbol] = w.name; });

  const formatPrice = (price: number): string => {
    if (price === 0) return '0.00';
    if (price >= 1) return price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const decimals = Math.max(2, -Math.floor(Math.log10(price)) + 2);
    return price.toFixed(Math.min(decimals, 8));
  };

  const getSignalColor = (signal: string) => {
    if (signal === 'BUY') return 'text-green-500';
    if (signal === 'SELL') return 'text-red-500';
    return 'text-gray-400';
  };

  const getSignalIcon = (signal: string) => {
    if (signal === 'BUY') return <TrendingUp className="w-5 h-5" />;
    if (signal === 'SELL') return <TrendingDown className="w-5 h-5" />;
    return <Minus className="w-5 h-5" />;
  };

  const getTrendBadge = (trend: string) => {
    const colors: Record<string, string> = {
      UPTREND: 'bg-green-500/10 text-green-500',
      DOWNTREND: 'bg-red-500/10 text-red-500',
      NEUTRAL: 'bg-gray-500/10 text-gray-400',
    };
    return colors[trend] || colors.NEUTRAL;
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div className="flex gap-1.5">
          {TIMEFRAMES.map(tf => (
            <button
              key={tf}
              onClick={() => setSelectedTf(tf)}
              className={`px-3 py-1.5 rounded-md text-sm font-mono font-semibold transition-all ${
                selectedTf === tf
                  ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25'
                  : 'bg-gray-800 text-gray-500 border border-gray-700 hover:text-white hover:border-gray-500'
              }`}
            >
              {tf}
            </button>
          ))}
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-1 px-3 py-1.5 bg-gray-800 border border-gray-700 hover:border-gray-600 rounded text-xs text-gray-300 hover:text-white transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      <div className="flex items-center gap-3 mb-3 px-3 py-2 bg-blue-500/5 border border-blue-500/10 rounded-lg">
        <span className="text-xs text-gray-400">Order Blocks, FVGs, Break of Structure</span>
        <a href="/guides/guide-smc.html" target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded text-xs font-medium transition-colors whitespace-nowrap">
          <BookOpen className="w-3.5 h-3.5" />
          How to read this
        </a>
      </div>

      {lastRefresh && (
        <div style={{ marginBottom: 12, fontSize: 11, color: '#52525b' }}>
          Updated: {lastRefresh.toLocaleTimeString()} &middot; Auto-refreshes every 2 min
        </div>
      )}

      {loading && data.length === 0 && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-7 h-7 text-blue-500 animate-spin mr-3" />
          <span className="text-gray-400 text-sm">Analyzing smart money concepts...</span>
        </div>
      )}

      <div className="grid gap-5">
        {data.map(coin => {
          const tfData = coin.timeframes?.[selectedTf];
          if (!tfData) return null;

          return (
            <div key={coin.symbol} className="bg-gray-900 border border-gray-800 rounded-lg p-5 hover:border-gray-700 transition-colors">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold">{coin.symbol}-USD</h2>
                  <p className="text-gray-400 text-xs">{nameMap[coin.symbol] || coin.symbol}</p>
                </div>
                <div className="text-right">
                  <div className="text-xl font-mono">${formatPrice(coin.price)}</div>
                  <div className={`text-xs ${coin.change24h >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {coin.change24h >= 0 ? '+' : ''}{coin.change24h.toFixed(2)}%
                  </div>
                </div>
              </div>

              {tfData.error && (
                <div className="flex items-center gap-2 mb-3 p-2 bg-yellow-500/10 border border-yellow-500/20 rounded text-xs text-yellow-400">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {tfData.error}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Signal + Structure */}
                <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                  <div className="text-xs text-gray-400 uppercase mb-2">Signal &amp; Structure <Tip text="Based on confluence of Order Blocks, structural breaks, and Fair Value Gaps. 50%+ confluence in one direction triggers a signal." /></div>
                  <div className={`flex items-center gap-2 mb-3 ${getSignalColor(tfData.signal)}`}>
                    {getSignalIcon(tfData.signal)}
                    <span className="font-bold">{tfData.signal}</span>
                    <span className="ml-auto text-xs text-gray-400">Confidence <Tip text="Confluence score (0-100%): OB nearby (+33%), BOS confirms (+33%), FVG present (+17%), CHoCH confirms (+17%). Higher = more factors aligned." /> <span className="font-mono">{tfData.confidence}%</span></span>
                  </div>

                  <div className={`text-xs px-2 py-0.5 rounded text-center mb-3 ${getTrendBadge(tfData.trend)}`}>
                    {tfData.trend}
                  </div>

                  {/* BOS / CHoCH */}
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Last BOS <Tip text="Break of Structure — trend CONTINUATION. Bullish BOS = new higher high (uptrend continues). Bearish BOS = new lower low (downtrend continues)." /></span>
                      <span className={tfData.structure.last_bos?.direction === 'bullish' ? 'text-green-400' : tfData.structure.last_bos?.direction === 'bearish' ? 'text-red-400' : 'text-gray-600'}>
                        {tfData.structure.last_bos?.direction?.toUpperCase() || 'None'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Last CHoCH <Tip text="Change of Character — potential trend REVERSAL. Bullish CHoCH = price breaks above a lower high during a downtrend. Bearish CHoCH = breaks below a higher low during uptrend." /></span>
                      <span className={tfData.structure.last_choch?.direction === 'bullish' ? 'text-green-400' : tfData.structure.last_choch?.direction === 'bearish' ? 'text-red-400' : 'text-gray-600'}>
                        {tfData.structure.last_choch?.direction?.toUpperCase() || 'None'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Order Blocks + FVGs */}
                <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                  <div className="text-xs text-gray-400 uppercase mb-2">Key Levels</div>

                  {/* Order Blocks */}
                  <div className="mb-3">
                    <div className="text-[10px] text-gray-500 uppercase mb-1">Order Blocks <Tip text="Zones where institutions placed large orders. Bullish OB (green) = the last red candle before a strong surge — acts as future support. Bearish OB (red) = last green candle before a drop — future resistance." /></div>
                    {tfData.order_blocks.length === 0 ? (
                      <div className="text-xs text-gray-600">No OBs detected</div>
                    ) : (
                      <div className="space-y-0.5">
                        {tfData.order_blocks.slice(0, 4).map((ob, i) => (
                          <div key={i} className="flex items-center justify-between text-xs">
                            <span className={ob.type === 'bullish' ? 'text-green-400' : 'text-red-400'}>
                              {ob.type === 'bullish' ? '\u25B2' : '\u25BC'} {ob.type}
                            </span>
                            <span className="font-mono text-gray-300">${formatPrice(ob.price)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* FVGs */}
                  <div>
                    <div className="text-[10px] text-gray-500 uppercase mb-1">Fair Value Gaps <Tip text="Price imbalances where the market moved too fast, leaving a gap. Bullish FVG = gap up (price tends to revisit before continuing). Bearish FVG = gap down. Unfilled FVGs act as price magnets." /></div>
                    {tfData.fvg.length === 0 ? (
                      <div className="text-xs text-gray-600">No FVGs detected</div>
                    ) : (
                      <div className="flex gap-1 flex-wrap">
                        {tfData.fvg.slice(0, 5).map((f, i) => (
                          <span
                            key={i}
                            className={`px-1.5 py-0.5 rounded text-[10px] ${
                              f.type === 'bullish'
                                ? 'bg-green-500/10 text-green-400'
                                : 'bg-red-500/10 text-red-400'
                            }`}
                          >
                            {f.type}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Liquidity */}
                  {tfData.liquidity_levels.length > 0 && (
                    <div className="mt-2">
                      <div className="text-[10px] text-gray-500 uppercase mb-1">Liquidity <Tip text="Price levels where stop-loss orders cluster (equal highs or equal lows). Smart money often pushes price through these to trigger stops before reversing." /></div>
                      <div className="flex gap-1 flex-wrap">
                        {tfData.liquidity_levels.map((lev, i) => (
                          <span key={i} className="px-1.5 py-0.5 rounded text-[10px] bg-yellow-500/10 text-yellow-400 font-mono">
                            ${formatPrice(lev)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Trade Setup */}
                <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-gray-400 uppercase">Trade Setup <Tip text="When enough confluences align (OB + structure + FVG), the system suggests an entry zone near the key Order Block, stop loss 3% beyond, and take profit at the nearest opposing level." /></span>
                    {tfData.conviction === 'HIGH' && (
                      <span className="px-1.5 py-0.5 bg-green-500/15 text-green-400 rounded text-[10px] font-bold border border-green-500/20">HIGH CONVICTION</span>
                    )}
                    {tfData.conviction === 'LOW' && (
                      <span className="px-1.5 py-0.5 bg-yellow-500/15 text-yellow-500 rounded text-[10px] font-bold border border-yellow-500/20">LOW CONVICTION</span>
                    )}
                    {tfData.conviction && (
                      <Tip text="HIGH = confidence ≥ 67% + R:R ≥ 2.0 + trend aligns with signal (e.g. UPTREND + BUY). LOW = signal fired but missing one or more of those conditions." />
                    )}
                  </div>
                  {tfData.entry_zone ? (
                    <div>
                      {/* R:R large display */}
                      {tfData.risk_reward && (
                        <div className="text-center mb-3">
                          <div className={`text-3xl font-bold font-mono ${
                            tfData.risk_reward >= 3 ? 'text-green-400' : tfData.risk_reward >= 1.5 ? 'text-yellow-400' : 'text-red-400'
                          }`}>
                            {tfData.risk_reward.toFixed(1)}R
                          </div>
                          <div className="text-[10px] text-gray-500">Risk/Reward</div>
                        </div>
                      )}

                      <div className="space-y-2 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Entry Zone <Tip text="Derived from the nearest Order Block. For longs, this is the bullish OB price. For shorts, the bearish OB price." /></span>
                          <span className="font-mono text-blue-400">${formatPrice(tfData.entry_zone)}</span>
                        </div>
                        {tfData.stop_loss && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Stop Loss</span>
                            <span className="font-mono text-red-400">${formatPrice(tfData.stop_loss)}</span>
                          </div>
                        )}
                        {tfData.take_profit && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Take Profit</span>
                            <span className="font-mono text-green-400">${formatPrice(tfData.take_profit)}</span>
                          </div>
                        )}
                      </div>

                      <div className="mt-2 p-2 rounded bg-blue-500/5 border border-blue-500/10">
                        <div className="text-[10px] text-blue-400 text-center">
                          {tfData.signal === 'BUY' ? 'Look for long entry near bullish OB' :
                           tfData.signal === 'SELL' ? 'Look for short entry near bearish OB' :
                           'No active setup'}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-20">
                      <span className="text-xs text-gray-600">No active setup</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SmartMoneyTab;
